import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  RepeatScopeProvider,
  useDocument,
  useIsReadOnly,
  useRepeatScope,
} from '../DocumentView/DocumentContext';
import DocumentElement from '../DocumentView/DocumentElement';

interface Props {
  element: DocumentElementType;
  /** Resolved title (already merged from data.label by the caller). */
  title: string;
  /** Optional resolved description. */
  description: string;
  /** From data.maxRepeats. Empty / 0 / negative ⇒ unlimited. */
  maxRepeats?: number;
  /**
   * Singular noun for one repeat (e.g. "Room"). Used in the Add button and
   * per-instance headings. Falls back to `title` when not set.
   */
  repeatItemName?: string;
}

/**
 * Fill-mode renderer for a repeatable section. Reads `values[...path, sectionId]`
 * as an array of per-instance dicts (defaults to one empty instance) and
 * renders each one inside its own RepeatScopeProvider so child fields
 * read/write into that instance's slice.
 */
const RepeatableSectionFiller: React.FC<Props> = ({
  element,
  title,
  description,
  maxRepeats,
  repeatItemName,
}) => {
  // The singular noun used for buttons + per-row labels. Defaults to the
  // section title (matches v1 behaviour) when no explicit item name is set.
  const itemName = repeatItemName || title;
  const { getAtPath, setAtPath } = useDocument();
  const isReadOnly = useIsReadOnly();
  const parentScope = useRepeatScope();
  const sectionPath = [...parentScope, element.id];

  const rawArray = getAtPath(sectionPath);
  const instances: Record<string, unknown>[] = Array.isArray(rawArray)
    ? (rawArray as Record<string, unknown>[])
    : [{}];

  // Persist a default singleton on first render so removeAt with one row
  // doesn't leave the array empty (we always keep ≥ 1 instance).
  // We don't proactively write here — Document context handles undefined as
  // an empty object; this just affects display.

  const limit =
    typeof maxRepeats === 'number' && maxRepeats > 0 ? maxRepeats : Infinity;
  const canAdd = instances.length < limit;
  const canRemove = instances.length > 1;

  const addInstance = useCallback(() => {
    const next = [...instances, {}];
    setAtPath(sectionPath, next);
  }, [instances, sectionPath, setAtPath]);

  const removeInstance = useCallback(
    (idx: number) => {
      const next = instances.filter((_, i) => i !== idx);
      setAtPath(sectionPath, next.length > 0 ? next : [{}]);
    },
    [instances, sectionPath, setAtPath],
  );

  const children = element.elements || [];

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-base font-semibold">{title}</Label>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      <Separator />

      {instances.map((_, idx) => (
        <div
          key={idx}
          className="rounded-md border border-border p-4 space-y-3 bg-muted/20"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {itemName} {idx + 1}
            </span>
            {!isReadOnly && canRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeInstance(idx)}
                className="h-7 px-2 text-muted-foreground hover:text-destructive"
                type="button"
              >
                <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5 mr-1" />
                Remove
              </Button>
            )}
          </div>
          <RepeatScopeProvider sectionId={element.id} index={idx}>
            <div className="space-y-4 pl-4 border-l-2 border-muted">
              {children.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-4">
                  No elements in this section
                </p>
              ) : (
                children.map((child) => (
                  <DocumentElement key={child.id} element={child} />
                ))
              )}
            </div>
          </RepeatScopeProvider>
        </div>
      ))}

      {!isReadOnly && (
        <Button
          variant="outline"
          size="sm"
          onClick={addInstance}
          disabled={!canAdd}
          type="button"
          className="gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
          Add {itemName}
        </Button>
      )}
      {!isReadOnly && !canAdd && limit !== Infinity && (
        <p className="text-xs text-muted-foreground">
          Maximum of {limit} {limit === 1 ? 'entry' : 'entries'} reached.
        </p>
      )}
    </div>
  );
};

export default RepeatableSectionFiller;
